import type {
  AddOnOption,
  EventStayPeriod,
  FlightSegment,
  PaymentOption,
} from "@/lib/api/types"
import type { BookingState } from "@/types/booking"

export function getSelectedStayPeriod(
  state: BookingState
): EventStayPeriod | null {
  if (!state.event || state.selectedStayPeriodIndex === null) return null

  return state.event.stayPeriods[state.selectedStayPeriodIndex] ?? null
}

/**
 * Drops add-ons that belong to a different stay — a Vienna shuttle arranged
 * for the long stay has no business showing on the short one. An add-on
 * without dates belongs to every stay, which is all of them until someone
 * ties one down in the admin.
 *
 * Until a stay period is chosen there is nothing to compare against, so
 * everything stays.
 */
export function addOnsForStay(
  addOns: AddOnOption[],
  stay: { stayStartDate: string; stayEndDate: string } | null
): AddOnOption[] {
  if (!stay) return addOns

  const start = stay.stayStartDate.slice(0, 10)
  const end = stay.stayEndDate.slice(0, 10)

  return addOns.filter((addOn) => {
    if (!addOn.stayStartDate || !addOn.stayEndDate) return true

    return addOn.stayStartDate.slice(0, 10) === start && addOn.stayEndDate.slice(0, 10) === end
  })
}

/** Event add-ons on offer for the stay period the traveller picked. */
export function getEventAddOns(state: BookingState): AddOnOption[] {
  return addOnsForStay(state.event?.addOns ?? [], getSelectedStayPeriod(state))
}

/**
 * Puts add-ons in the order set in the admin.
 *
 * Event and hotel add-ons arrive from two endpoints and are shown as one
 * list, so they are merged first and sorted after — the order has to read as
 * one list, not as two ordered halves stuck together.
 *
 * Equal positions keep the order they came in, which is the sort's own
 * guarantee and means an untouched list looks exactly as it did before
 * anyone set a position.
 */
export function sortAddOns(addOns: AddOnOption[]): AddOnOption[] {
  return [...addOns].sort((a, b) => a.position - b.position)
}

export function getAvailableAddOns(state: BookingState): AddOnOption[] {
  const addOns = [
    ...getEventAddOns(state),
    ...(state.packageType !== "flight-only"
      ? (state.hotelAddOns[state.selectedHotel?.hotelId ?? ""] ?? [])
      : []),
  ]

  const seen = new Set<string>()

  return sortAddOns(
    addOns.filter((addOn) => {
      if (seen.has(addOn.id)) return false
      seen.add(addOn.id)
      return true
    })
  )
}

export function isTransferAddOn(state: BookingState, addOnId: string): boolean {
  return getAvailableAddOns(state).some(
    (a) => a.id === addOnId && (a.type ?? "").toLowerCase().includes("transfer")
  )
}

export function addOnQuantityLimit(state: BookingState, addOnId: string): number {
  if (isTransferAddOn(state, addOnId)) return 99
  return state.passengerCount
}

export function getSelectedAddOns(state: BookingState) {
  const addOnsById = new Map(
    getAvailableAddOns(state).map((addOn) => [addOn.id, addOn])
  )

  return Object.entries(state.selectedAddOns)
    .filter(([, quantity]) => quantity > 0)
    .flatMap(([addOnId, quantity]) => {
      const addOn = addOnsById.get(addOnId)
      return addOn ? [{ addOn, quantity }] : []
    })
}

export function getPackagePricePerPerson(state: BookingState) {
  if (state.packageType === "flight-only") {
    const base = state.flightBasePackagePrice ?? state.offers?.basePackagePricePerPerson ?? 0
    return Math.max(
      base + (state.selectedFlight?.priceDiffPerPerson ?? 0),
      0
    )
  }

  if (!state.offers || !state.selectedHotel) return 0

  return Math.max(
    state.offers.basePackagePricePerPerson +
      state.selectedHotel.priceDiffPerPerson +
      (state.packageType === "flight-hotel"
        ? (state.selectedFlight?.priceDiffPerPerson ?? 0)
        : 0),
    0
  )
}

export function getAddOnsTotal(state: BookingState) {
  return getSelectedAddOns(state).reduce(
    (total, { addOn, quantity }) =>
      total + addOn.priceDeltaPerPerson * quantity,
    0
  )
}

export function getGrandTotal(state: BookingState) {
  return (
    getPackagePricePerPerson(state) * state.passengerCount +
    getAddOnsTotal(state)
  )
}

export type StayPeriodLabel =
  | "most-popular"
  | "full-event-stay"
  | "arrive-early"
  | "leave-late"
  | "flexible-dates"

const STAY_PERIOD_LABELS: readonly string[] = [
  "most-popular",
  "full-event-stay",
  "arrive-early",
  "leave-late",
  "flexible-dates",
]

/**
 * The badge for a stay-period tile: the one set in the admin if there is one,
 * otherwise worked out from the dates.
 *
 * An unknown value coming from the API is ignored rather than rendered — this
 * is the only place that decides a tile's badge, so a typo upstream should
 * fall back to the old behaviour, not print itself on the page.
 */
export function resolveStayPeriodLabel(
  period: { stayStartDate: string; stayEndDate: string; label?: string | null },
  eventStartDate: string | null | undefined,
  eventEndDate: string | null | undefined,
  allPeriods?: { stayStartDate: string; stayEndDate: string }[]
): StayPeriodLabel | null {
  if (period.label && STAY_PERIOD_LABELS.includes(period.label)) {
    return period.label as StayPeriodLabel
  }

  return getStayPeriodLabel(
    period.stayStartDate,
    period.stayEndDate,
    eventStartDate,
    eventEndDate,
    allPeriods
  )
}

export function getStayPeriodLabel(
  stayStartDate: string,
  stayEndDate: string,
  eventStartDate: string | null | undefined,
  eventEndDate: string | null | undefined,
  allPeriods?: { stayStartDate: string; stayEndDate: string }[]
): StayPeriodLabel | null {
  if (!eventStartDate || !eventEndDate) return null

  const sStart = stayStartDate.slice(0, 10)
  const sEnd = stayEndDate.slice(0, 10)
  const eStart = eventStartDate.slice(0, 10)
  const eEnd = eventEndDate.slice(0, 10)

  const startMatch = sStart === eStart
  const endMatch = sEnd === eEnd
  const coversEvent = sStart <= eStart && sEnd >= eEnd

  if (startMatch && endMatch) return "most-popular"

  const hasExactMatch = allPeriods?.some((p) => {
    return p.stayStartDate.slice(0, 10) === eStart && p.stayEndDate.slice(0, 10) === eEnd
  }) ?? false

  const nextDay = new Date(`${eEnd}T12:00:00Z`)
  nextDay.setUTCDate(nextDay.getUTCDate() + 1)
  const nextDayAfterEvent = nextDay.toISOString().slice(0, 10)

  if (!hasExactMatch && startMatch && sEnd === nextDayAfterEvent) return "full-event-stay"
  if (coversEvent && !hasExactMatch) return "most-popular"
  if (!startMatch && sStart < eStart && endMatch) return "arrive-early"
  if (startMatch && !endMatch && sEnd > eEnd) return "leave-late"
  if (coversEvent && hasExactMatch && sStart < eStart && sEnd > eEnd) return "full-event-stay"

  return "flexible-dates"
}

export function formatDuration(minutes: number) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

export function formatMoney(value: number, currency: string) {
  const formatted = Number.isInteger(value)
    ? value.toFixed(0)
    : value.toFixed(2)
  return `${formatted.replace(".", ",")} ${currency}`
}

export function formatPriceDelta(
  value: number,
  currency: string,
  showZeroAsDiff = false
) {
  if (value === 0)
    return showZeroAsDiff
      ? `+${formatMoney(0, currency)}`
      : `Included in package`
  if (value > 0) return `+${formatMoney(value, currency)}`
  return `-${formatMoney(Math.abs(value), currency)}`
}

export function formatDateLabel(date: string | null | undefined) {
  if (!date) return "TBA"

  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })
}

export function formatDayLabel(date: string | null | undefined) {
  if (!date) return ""

  return new Date(date)
    .toLocaleDateString("en-US", { weekday: "short" })
    .toUpperCase()
}

export function dayOffset(segment: FlightSegment): number {
  return Math.round(
    (Date.parse(segment.arrivalDate) - Date.parse(segment.departureDate)) /
      86_400_000
  )
}

export function formatDateRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined
) {
  if (!startDate || !endDate) return "Dates available on request"

  const start = new Date(startDate)
  const end = new Date(endDate)
  const startMonth = start.toLocaleDateString("en-US", { month: "long" }).toUpperCase()
  const endMonth = end.toLocaleDateString("en-US", { month: "long" }).toUpperCase()
  const startDay = start.getDate()
  const endDay = end.getDate()
  const year = end.getFullYear()

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay} – ${endDay}, ${year}`
  }
  return `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${year}`
}

export function getNightCount(
  startDate: string | null | undefined,
  endDate: string | null | undefined
) {
  if (!startDate || !endDate) return 0

  const start = new Date(startDate)
  const end = new Date(endDate)
  return Math.max(
    Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)),
    0
  )
}

export function getActivePaymentOption(
  state: BookingState,
  payLaterEnabled: boolean
): PaymentOption {
  if (payLaterEnabled && state.offers?.payLater) {
    return state.offers.payLater.option
  }

  return "FULL"
}

export function getDestinationAirportCode(state: BookingState): string | null {
  return state.destinationAirport?.code ?? state.event?.airport ?? null
}
